import React, { useEffect, useMemo, useState } from 'react';
import { Icon } from '@iconify/react';
import {
  applyPriceAdjustment,
  formatAdminPrice,
  PERCENT_PRESETS,
} from '../../utils/adminBulkPriceAdjust';
import { AppAlert, AppConfirmModal } from '../ui';

/**
 * Panneau d'augmentation groupée des prix, pensé pour des utilisateurs non techniques.
 *
 * @param {object} props
 * @param {string} props.title
 * @param {string} [props.description]
 * @param {Array<{ id: string, groupLabel: string, groupSublabel?: string, fieldLabel?: string, value: number, unit?: string }>} props.items
 * @param {(updates: Array<{ id: string, oldValue: number, newValue: number }>) => Promise<void>} props.onApply
 * @param {boolean} [props.disabled]
 * @param {boolean} [props.dolibarrPushEnabled]
 * @param {boolean} [props.hideHeader] — Masque le bandeau titre/bouton (déclencheur externe).
 * @param {boolean} [props.expanded] — Contrôle externe de l'ouverture (avec onExpandedChange).
 * @param {(next: boolean) => void} [props.onExpandedChange]
 * @param {string} [props.className]
 */
const AdminBulkPriceAdjustPanel = ({
  title = 'Augmentation groupée des prix',
  description = 'Appliquez la même augmentation à plusieurs tarifs en une seule opération. Les montants sont arrondis au 0 ou 5 supérieur (ex. 11 € → 15 €). Vérifiez l’aperçu avant de valider.',
  items = [],
  onApply,
  disabled = false,
  dolibarrPushEnabled = false,
  hideHeader = false,
  expanded: controlledExpanded,
  onExpandedChange,
  className = '',
}) => {
  const [internalExpanded, setInternalExpanded] = useState(false);
  const [mode, setMode] = useState('percent');
  const [amount, setAmount] = useState('5');
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [applying, setApplying] = useState(false);

  const isControlled = controlledExpanded !== undefined;
  const expanded = isControlled ? Boolean(controlledExpanded) : internalExpanded;

  const setExpanded = (next) => {
    const value = typeof next === 'function' ? next(expanded) : next;
    if (!isControlled) setInternalExpanded(value);
    onExpandedChange?.(value);
  };

  const eligibleItems = useMemo(
    () => items.filter((item) => item.value != null && Number(item.value) > 0),
    [items],
  );

  useEffect(() => {
    setSelectedIds(new Set(eligibleItems.map((item) => item.id)));
  }, [eligibleItems]);

  const parsedAmount = Number(String(amount).replace(',', '.'));
  const amountValid = parsedAmount > 0;

  const allPreviewRows = useMemo(() => {
    if (!amountValid) return [];

    return eligibleItems.map((item) => {
      const oldValue = Number(item.value) || 0;
      const newValue = applyPriceAdjustment(oldValue, { mode, amount: parsedAmount });
      return { ...item, oldValue, newValue, changed: newValue !== oldValue };
    });
  }, [eligibleItems, mode, parsedAmount, amountValid]);

  const previewRows = useMemo(
    () => allPreviewRows.filter((row) => selectedIds.has(row.id) && row.changed),
    [allPreviewRows, selectedIds],
  );

  const toggleAll = (checked) => {
    setSelectedIds(checked ? new Set(eligibleItems.map((item) => item.id)) : new Set());
  };

  const toggleOne = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleApply = async () => {
    if (!previewRows.length || !onApply) return;
    setApplying(true);
    try {
      await onApply(previewRows.map(({ id, oldValue, newValue }) => ({ id, oldValue, newValue })));
      setConfirmOpen(false);
      setExpanded(false);
      setAmount(mode === 'percent' ? '5' : '');
    } finally {
      setApplying(false);
    }
  };

  if (eligibleItems.length === 0) return null;
  if (hideHeader && !expanded) return null;

  const allSelected = selectedIds.size === eligibleItems.length && eligibleItems.length > 0;
  const modeLabel = mode === 'percent' ? `${parsedAmount} %` : `${parsedAmount} €`;

  const body = (
    <div className={`admin-bulk-price__body${hideHeader ? '' : ' mt-3 pt-3 border-top'}`}>
      {!dolibarrPushEnabled && (
        <AppAlert variant="info" className="text-sm py-2 mb-3">
          Mode local uniquement : les prix seront mis à jour sur Konvergence (tarifs + miroir catalogue).
          Ils ne seront <strong>pas</strong> envoyés à Dolibarr tant que
          {' '}
          <code>DOLIBARR_PRICE_PUSH_ENABLED</code>
          {' '}
          est désactivé. Une sync catalogue Dolibarr peut réécraser le miroir.
        </AppAlert>
      )}
      {dolibarrPushEnabled && (
        <AppAlert variant="warning" className="text-sm py-2 mb-3">
          Push Dolibarr activé : les nouveaux prix seront aussi écrits dans Dolibarr.
        </AppAlert>
      )}
      <div className="row g-3 align-items-end">
        <div className="col-md-5">
          <label className="form-label fw-medium">Type d&apos;augmentation</label>
          <div className="btn-group w-100" role="group" aria-label="Type d'augmentation">
            <button
              type="button"
              className={`btn ${mode === 'percent' ? 'btn-danger-600' : 'btn-outline-secondary'}`}
              onClick={() => setMode('percent')}
            >
              En pourcentage (%)
            </button>
            <button
              type="button"
              className={`btn ${mode === 'fixed' ? 'btn-danger-600' : 'btn-outline-secondary'}`}
              onClick={() => setMode('fixed')}
            >
              Montant fixe (€)
            </button>
          </div>
        </div>
        <div className="col-md-3">
          <label className="form-label fw-medium">
            {mode === 'percent' ? 'Pourcentage' : 'Montant à ajouter'}
          </label>
          <div className="input-group">
            <input
              type="number"
              min="0.01"
              step={mode === 'percent' ? '0.1' : '0.01'}
              className="form-control form-control-lg admin-bulk-price__amount-input"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder={mode === 'percent' ? '5' : '10'}
            />
            <span className="input-group-text">{mode === 'percent' ? '%' : '€'}</span>
          </div>
        </div>
        {mode === 'percent' && (
          <div className="col-md-4">
            <label className="form-label fw-medium">Raccourcis</label>
            <div className="d-flex flex-wrap gap-2">
              {PERCENT_PRESETS.map((preset) => (
                <button
                  key={preset}
                  type="button"
                  className={`btn btn-sm ${Number(amount) === preset ? 'btn-danger-600' : 'btn-outline-secondary'}`}
                  onClick={() => setAmount(String(preset))}
                >
                  +{preset} %
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mt-3 mb-2">
        <div className="form-check mb-0">
          <input
            className="form-check-input"
            type="checkbox"
            id="bulk-price-select-all"
            checked={allSelected}
            onChange={(e) => toggleAll(e.target.checked)}
          />
          <label className="form-check-label" htmlFor="bulk-price-select-all">
            Tout sélectionner ({eligibleItems.length} tarif{eligibleItems.length > 1 ? 's' : ''})
          </label>
        </div>
        {amountValid && previewRows.length > 0 && (
          <span className="badge bg-success-100 text-success-600">
            {previewRows.length} modification{previewRows.length > 1 ? 's' : ''} prévue{previewRows.length > 1 ? 's' : ''}
          </span>
        )}
      </div>

      {!amountValid && (
        <AppAlert variant="light" className="border text-sm mb-0">
          Saisissez un {mode === 'percent' ? 'pourcentage' : 'montant'} supérieur à 0 pour voir l&apos;aperçu.
        </AppAlert>
      )}

      {amountValid && selectedIds.size === 0 && (
        <AppAlert variant="light" className="border text-sm mb-2">
          Aucun tarif sélectionné — cochez les lignes à inclure dans l&apos;augmentation.
        </AppAlert>
      )}

      {amountValid && (
        <div className="admin-bulk-price__preview table-responsive">
          <table className="table table-sm table-hover mb-0">
            <thead>
              <tr>
                <th style={{ width: 36 }} aria-label="Sélection" />
                <th>Tarif</th>
                <th className="text-end">Avant</th>
                <th className="text-end">Après</th>
                <th className="text-end">Écart</th>
              </tr>
            </thead>
            <tbody>
              {allPreviewRows.map((row) => {
                const checked = selectedIds.has(row.id);
                const unit = row.unit ?? '€';
                return (
                  <tr key={row.id} className={!checked ? 'opacity-50' : ''}>
                    <td>
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={checked}
                        onChange={() => toggleOne(row.id)}
                        aria-label={`Sélectionner ${row.groupLabel}`}
                      />
                    </td>
                    <td>
                      <div className="fw-medium">{row.groupLabel}</div>
                      {(row.fieldLabel || row.groupSublabel) && (
                        <div className="text-xs text-secondary-light">
                          {[row.fieldLabel, row.groupSublabel].filter(Boolean).join(' · ')}
                        </div>
                      )}
                    </td>
                    <td className="text-end">{formatAdminPrice(row.oldValue, unit)}</td>
                    <td className="text-end fw-semibold text-success-600">
                      {row.changed ? formatAdminPrice(row.newValue, unit) : '—'}
                    </td>
                    <td className="text-end text-secondary-light">
                      {row.changed ? (
                        <>+{formatAdminPrice(row.newValue - row.oldValue, unit)}</>
                      ) : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="d-flex flex-wrap justify-content-end gap-2 mt-3">
        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={() => setExpanded(false)}
        >
          Annuler
        </button>
        <button
          type="button"
          className="btn btn-danger-600"
          disabled={disabled || applying || !amountValid || previewRows.length === 0}
          onClick={() => setConfirmOpen(true)}
        >
          <Icon icon="mdi:check-circle-outline" className="me-1" />
          Appliquer {previewRows.length > 0 ? `(${previewRows.length})` : ''}
        </button>
      </div>
    </div>
  );

  return (
    <>
      {hideHeader ? (
        <div className={`admin-bulk-price admin-bulk-price--embedded px-3 py-3 border-bottom${className ? ` ${className}` : ''}`}>
          {body}
        </div>
      ) : (
        <div className={`admin-bulk-price card mb-3${className ? ` ${className}` : ''}`}>
          <div className="card-body">
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
              <div className="d-flex align-items-center admin-bulk-price__intro">
                <span className="admin-bulk-price__icon" aria-hidden>
                  <Icon icon="mdi:trending-up" width={22} />
                </span>
                <div>
                  <h6 className="mb-1 fw-semibold">{title}</h6>
                  <p className="text-sm text-secondary-light mb-0">{description}</p>
                </div>
              </div>
              <button
                type="button"
                className={`btn btn-sm ${expanded ? 'btn-outline-secondary' : 'btn-danger-600'}`}
                disabled={disabled}
                onClick={() => setExpanded((v) => !v)}
              >
                <Icon icon={expanded ? 'mdi:chevron-up' : 'mdi:calculator-variant'} className="me-1" />
                {expanded ? 'Masquer' : 'Augmenter les prix…'}
              </button>
            </div>

            {expanded && body}
          </div>
        </div>
      )}

      <AppConfirmModal
        show={confirmOpen}
        title="Confirmer l'augmentation"
        onClose={() => setConfirmOpen(false)}
        cancelLabel="Retour"
        confirmLabel="Confirmer et enregistrer"
        confirmVariant="success"
        saving={applying}
        savingLabel="Enregistrement…"
        onConfirm={handleApply}
      >
        <p>
          Vous allez augmenter <strong>{previewRows.length} tarif{previewRows.length > 1 ? 's' : ''}</strong>
          {' '}de <strong>{modeLabel}</strong>.
        </p>
        <AppAlert variant="warning" className="text-sm py-2 mb-0">
          {dolibarrPushEnabled
            ? 'Cette action met à jour les prix en local et les pousse vers Dolibarr. Vérifiez l’aperçu avant de confirmer.'
            : 'Cette action met à jour les prix en local uniquement (pas d’écriture Dolibarr). Vérifiez l’aperçu avant de confirmer.'}
        </AppAlert>
      </AppConfirmModal>
    </>
  );
};

export default AdminBulkPriceAdjustPanel;
